import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiSearch, FiXCircle } from "react-icons/fi";
import { toJalaali } from "jalaali-js";
import { getLanguageDirection } from "../i18n/config";
import { PROXY } from "../lib/utils";
import CertificateFilePreviewModal from "../components/CertificateFilePreviewModal";
import { buildCertificateVerificationUrl } from "../lib/certificateVerificationUrl";
import PublicCertificateDetails from "../components/PublicCertificateDetails";

const CODE_PATTERN = /^[A-HJ-NP-Z2-9]{6}$/;
const normalizeCode = (value = "") => String(value).trim().toUpperCase();
const DEFAULT_CODE_PLACEHOLDER = "Enter certificate code";

const safePlaceholder = (value) => {
  if (typeof value !== "string") return DEFAULT_CODE_PLACEHOLDER;
  const normalized = value.trim();
  return normalized && !["null", "undefined"].includes(normalized.toLowerCase())
    ? normalized
    : DEFAULT_CODE_PLACEHOLDER;
};

const formatShamsiDate = (value) => {
  if (!value) return "";
  const date = new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return "";
  const jalali = toJalaali(date);
  return `${jalali.jy}/${String(jalali.jm).padStart(2, "0")}/${String(jalali.jd).padStart(2, "0")}`;
};

function CertificateVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryCode = searchParams.get("code");
  const [inputCode, setInputCode] = useState(normalizeCode(queryCode));
  const [status, setStatus] = useState(queryCode ? "loading" : "idle");
  const [certificate, setCertificate] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [shareMessage, setShareMessage] = useState("");
  const direction = getLanguageDirection(i18n.language);
  const codePlaceholder = safePlaceholder(t("certificateVerification.enterCode", { defaultValue: DEFAULT_CODE_PLACEHOLDER }));

  useEffect(() => {
    const code = normalizeCode(queryCode);
    setInputCode(code);

    if (!queryCode) {
      setStatus("idle");
      setCertificate(null);
      return undefined;
    }
    if (!CODE_PATTERN.test(code)) {
      setStatus("not-found");
      setCertificate(null);
      return undefined;
    }

    const controller = new AbortController();
    const loadCertificate = async () => {
      setStatus("loading");
      setCertificate(null);
      try {
        const response = await fetch(`${PROXY}/certificates/verify/${encodeURIComponent(code)}`, { signal: controller.signal });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || "Certificate not found");
        if (!controller.signal.aborted) {
          setCertificate(payload.certificate);
          setStatus("verified");
        }
      } catch (error) {
        if (!controller.signal.aborted) setStatus("not-found");
      }
    };

    loadCertificate();
    return () => controller.abort();
  }, [queryCode]);

  const submit = (event) => {
    event.preventDefault();
    const code = normalizeCode(inputCode);
    if (!CODE_PATTERN.test(code)) {
      setStatus("not-found");
      setCertificate(null);
      return;
    }
    navigate({ pathname: "/certificates/verify", search: `?code=${encodeURIComponent(code)}` });
  };

  const publicCertificate = useMemo(() => {
    if (!certificate) return null;
    const teacher = certificate.teacher || {};
    return {
      ...certificate,
      teacherName: teacher.fullName || certificate.teacherName || "",
      teacherPhotoUrl: teacher.photoUrl || certificate.teacherPhotoUrl || "",
      academicDegree: teacher.academicDegree || certificate.academicDegree || "",
      academicRanking: teacher.academicRanking || certificate.academicRanking || "",
      teacherBiographyDescription: teacher.biographyDescription || certificate.teacherBiographyDescription || "",
      teacherDocuments: teacher.documents || certificate.teacherDocuments || [],
      issueDateLabel: formatShamsiDate(certificate.issueDate),
      academicDegreeLabel: (teacher.academicDegree || certificate.academicDegree)
        ? t(`certificateVerification.degrees.${teacher.academicDegree || certificate.academicDegree}`, teacher.academicDegree || certificate.academicDegree)
        : "",
      academicRankingLabel: (teacher.academicRanking || certificate.academicRanking)
        ? t(`certificateVerification.rankings.${teacher.academicRanking || certificate.academicRanking}`, teacher.academicRanking || certificate.academicRanking)
        : "",
    };
  }, [certificate, t]);

  const shareUrl = useMemo(() => buildCertificateVerificationUrl(certificate?.code), [certificate?.code]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage(t("certificateVerification.linkCopied"));
    } catch {
      setShareMessage(t("certificateVerification.copyFailed"));
    }
  };

  const shareCertificate = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: t("certificateVerification.title"), url: shareUrl });
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }
    await copyLink();
  };

  return <section dir={direction} className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
      <div className="text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-600">HELP</p>
        <h1 className="mt-2 text-3xl font-bold text-slate-900">{t("certificateVerification.title")}</h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-600">{t("certificateVerification.subtitle")}</p>
      </div>
      <form onSubmit={submit} className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="flex-1 text-sm font-semibold text-slate-700">
          {t("certificateVerification.codeLabel")}
          <input
            value={inputCode}
            onChange={(event) => setInputCode(event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
            placeholder={codePlaceholder}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
            maxLength={6}
            className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 font-mono tracking-[0.2em] text-slate-900 outline-none focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100"
          />
        </label>
        <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700">
          <FiSearch /> {t("certificateVerification.verify")}
        </button>
      </form>
    </div>

    {status === "loading" ? <div role="status" className="mt-6 rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">{t("certificateVerification.loading")}</div> : null}
    {status === "not-found" ? <div role="alert" className="mt-6 rounded-3xl border border-rose-200 bg-rose-50 p-7 text-center"><FiXCircle className="mx-auto h-10 w-10 text-rose-600" /><h2 className="mt-3 text-xl font-bold text-rose-900">{t("certificateVerification.notFound")}</h2><p className="mt-2 text-sm text-rose-800">{t("certificateVerification.notFoundText")}</p></div> : null}
    {status === "verified" && publicCertificate ? <>
      <div className="mt-6 rounded-3xl border border-emerald-200 bg-emerald-50 px-5 py-5 text-center sm:px-8">
        <FiCheckCircle className="mx-auto h-11 w-11 text-emerald-600" />
        <h2 className="mt-2 text-2xl font-bold text-emerald-900">{t("certificateVerification.verified")}</h2>
      </div>
      <PublicCertificateDetails certificate={publicCertificate} onPreview={setPreviewFile} onShare={shareCertificate} t={t} />
    </> : null}
    {shareMessage ? <p role="status" className="mt-3 text-center text-sm text-indigo-800">{shareMessage}</p> : null}
    {previewFile && status === "verified" ? <CertificateFilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} onShare={shareCertificate} t={t} /> : null}
    <div className="mt-6 text-center"><Link to="/" className="text-sm font-semibold text-indigo-700 hover:underline">{t("certificateVerification.backHome")}</Link></div>
  </section>;
}

export default CertificateVerification;
